<!-- eslint-disable no-undef -->
<script setup>
  // Imports
  import { ref, computed, onMounted } from 'vue';
  import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue';
  import { ChevronUpDownIcon } from '@heroicons/vue/20/solid';
  import LoadingAnimation3 from '../loadingIcons/LoadingAnimation3.vue';
  import InfoIcon from '@/components/common/InfoIcon.vue';
  import Roles from '@/api/roles';
  import { useModalBackdropClose } from '@/composables/useModalBackdropClose';

  // Definitions
  const props = defineProps({
    userDetails: {
      type: Object,
      default: null
    }
  });

  const emit = defineEmits(['submitForm', 'close']);

  const loading = ref(false);
  const loadingMessage = ref('Creating User...');
  const error = ref('');

  // Form fields
  const firstName = ref(props.userDetails?.firstName || '');
  const lastName = ref(props.userDetails?.lastName || '');
  const email = ref(props.userDetails?.email || '');
  const entity = ref(props.userDetails?.entity || '');
  const role = ref(props.userDetails?.role || 'viewer');
  const roles = ref([]);
  const company = ref(props.userDetails?.company || '');
  const timeFormat = ref(props.userDetails?.timeFormat || '24Hour');
  const profilePic = ref(null);
  const profilePicPreview = ref(props.userDetails?.profilePic || null);

  const isEditMode = computed(() => props.userDetails !== null && Object.keys(props.userDetails).length > 0);

  onMounted(async () => {
    await loadRoles();
  });

  // Methods
  const validateForm = () => {
    error.value = '';
    
    if (!firstName.value || firstName.value.trim() === '') {
      error.value = 'First name is required';
      return false;
    }
    
    if (!lastName.value || lastName.value.trim() === '') {
      error.value = 'Last name is required';
      return false;
    }

    if (!email.value || email.value.trim() === '') {
      error.value = 'Email is required';
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.value)) {
      error.value = 'Please enter a valid email address';
      return false;
    }
    
    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    loading.value = true;
    loadingMessage.value = isEditMode.value ? 'Updating User...' : 'Creating User...';

    const formData = {
      firstName: firstName.value.trim(),
      lastName: lastName.value.trim(),
      email: email.value.trim(),
      entity: entity.value,
      role: role.value,
      permissions: roles.value.find(r => r.id === role.value).permissions,
      company: company.value.trim(),
      timeFormat: timeFormat.value,
      profilePic: profilePic.value
    };

    emit('submitForm', formData);
  };

  const loadRoles = async () => {
    try {
      const response = await Roles.get();
      roles.value = response.roles;
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  };

  const setFile = (type, event) => {
    const file = event.target.files[0];
    if (file) {
      if (type === 'profilePic') {
        profilePic.value = file;
        
        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          profilePicPreview.value = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const removeProfilePic = () => {
    profilePic.value = null;
    profilePicPreview.value = null;
  };

  const close = () => {
    loading.value = false;
    error.value = '';
    emit('close');
  };

  const { onBackdropMousedown, onBackdropMouseup, onContentMousedown } = useModalBackdropClose(close);
</script>

<template>
  <div class="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex justify-center items-center z-50" @mousedown.self="onBackdropMousedown" @mouseup.self="onBackdropMouseup">
    <div class="ui-modal p-6 rounded-lg w-[600px] max-h-[calc(100vh-50px)] overflow-y-auto shadow-2xl" @mousedown="onContentMousedown">
      <!-- Header -->
      <div class="mb-6">
        <div class="h-[3px] w-full bg-mc-red rounded-full mb-5"></div>
        <h2 v-if="!isEditMode" class="font-extrabold text-xl text-center mb-5 text-mc-ink dark:text-on-dark tracking-tight">Create New User</h2>
        <h2 v-else class="font-extrabold text-xl text-center mb-5 text-mc-ink dark:text-on-dark tracking-tight">Edit User</h2>
      </div>
      <form @submit.prevent="handleSubmit" class="space-y-6">
        <!-- First Name -->
        <div>
          <label for="first-name" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">First Name <span class="text-red-500">*</span></label>
          <input
            id="first-name"
            v-model="firstName"
            type="text"
            class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
            placeholder="Enter first name"
            required
          />
        </div>

        <!-- Last Name -->
        <div>
          <label for="last-name" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">Last Name <span class="text-red-500">*</span></label>
          <input
            id="last-name"
            v-model="lastName"
            type="text"
            class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
            placeholder="Enter last name"
            required
          />
        </div>

        <!-- Email -->
        <div>
          <label for="email" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">Email <span class="text-red-500">*</span></label>
          <input
            id="email"
            v-model="email"
            type="email"
            class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
            placeholder="user@example.com"
            required
          />
        </div>

        <!-- Role -->
        <div>
          <label for="role" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">
            <div class="flex items-center">
              <span>Role <span class="text-red-500">*</span></span>
              <InfoIcon
                class="ml-2"
                iconType="info"
                content="Select the role that defines the user's permissions and access level."
              />
              <LoadingAnimation3 v-if="!roles.length" size="20px" class="ml-2"/>
            </div>
          </label>
          <Listbox v-model="role" as="div" class="relative" :disabled="!roles.length">
            <ListboxButton class="w-full flex items-center justify-between p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20 disabled:opacity-50">
              <span>{{ roles.find(r => r.id === role)?.id.replace(/-/g, ' ') ?? role.replace(/-/g, ' ') }}</span>
              <ChevronUpDownIcon class="h-4 w-4 text-ink-muted dark:text-on-dark-muted shrink-0" aria-hidden="true" />
            </ListboxButton>
            <ListboxOptions class="absolute z-20 mt-1 w-full bg-mc-white dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-sm shadow-mc-md max-h-60 overflow-auto focus:outline-none">
              <ListboxOption v-for="r in roles" :key="r.id" :value="r.id" v-slot="{ active, selected }">
                <li :class="['px-3 py-2 cursor-pointer select-none text-mc-ink dark:text-on-dark', active ? 'bg-mc-steel/40 dark:bg-white/10' : '', selected ? 'font-semibold' : '']">
                  {{ r.id.replace(/-/g, ' ') }}
                </li>
              </ListboxOption>
            </ListboxOptions>
          </Listbox>
        </div>

        <!-- Profile Picture -->
        <div>
          <label for="profile-pic" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 flex items-center">
            Profile Picture
            <InfoIcon
              class="ml-2"
              iconType="info"
              content="Upload a profile picture for this user. Accepted formats: JPG, PNG, GIF"
            />
          </label>

          <div class="mt-2">
            <input
              id="profile-pic"
              type="file"
              accept="image/*"
              @change="setFile('profilePic', $event)"
              class="w-full p-2 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark"
            />
            
            <!-- Image Preview -->
            <div v-if="profilePicPreview" class="mt-4 flex items-center gap-4">
              <img 
                :src="profilePicPreview" 
                alt="Profile Preview" 
                class="w-20 h-20 rounded-full object-cover border-2 border-mc-steel dark:border-line-on-dark"
              />
              <button 
                type="button" 
                @click="removeProfilePic" 
                class="px-3 py-1 text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 border border-red-300 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30"
              >
                Remove
              </button>
            </div>
          </div>
        </div>

        <!-- Error Messages -->
        <div v-if="error" class="mt-5">
          <p class="font-normal text-red-600 dark:text-red-400 whitespace-pre-line bg-red-50 dark:bg-red-900/30 p-3 rounded-lg">
            {{ error }}
          </p>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-between items-center mt-8 pt-6 border-t border-mc-steel dark:border-line-on-dark">
          <button
            type="button"
            @click="close"
            class="px-5 py-2 text-xs rounded-full border border-mc-steel dark:border-white/20 text-ink-muted dark:text-on-dark-muted hover:bg-mc-steel/20 dark:hover:bg-white/10 transition-colors duration-160"
            :disabled="loading"
          >
            Cancel
          </button>
          
          <div class="flex items-center gap-3">
            <LoadingAnimation3 
              v-if="loading" 
              size="30px"
              :message="loadingMessage"
            />
            <button 
              v-else
              type="submit" 
              :disabled="loading || !firstName.trim() || !lastName.trim() || !email.trim()" 
              class="default-button" 
              :class="{'default-button-off': loading || !firstName.trim() || !lastName.trim() || !email.trim()}"
            >
              {{ isEditMode ? 'Update User' : 'Create User' }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>